<template>
  <div class="tag-list">
    <template v-if="!noResult">
      <div class="list-top">
        <button v-if="group && group.groupId > 1"
                class="add-tag"
                :class="{disabled: isDemo}"
                @click="addTagName">
          <ui-icon-tooltip v-if="realListData.length >= 16"
                           :iconSize="'outline-white'"
                           arrowBottom
                           arrowLeft>
            <span v-html="$t('group.editMode.tagLessThan16')"></span>
          </ui-icon-tooltip>
          {{ $t('group.editMode.addTag') }}
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
            <g fill-rule="evenodd" transform="translate(2 2)">
              <rect width="12" height="2" y="5" rx="1" />
              <rect width="12" height="2" y="5" rx="1" transform="rotate(90 6 6)" />
            </g>
          </svg>
        </button>
      </div>
      <div class="list-body">
        <div class="thead">
          <table>
            <colgroup>
              <col>
              <col style="width: 140px">
            </colgroup>
            <thead>
              <tr>
                <th v-for="column in columns"
                    :key="`tag-list-${column.text}-${column.value}`"
                    :class="[{ active: listData.length && computedSort === column.value },
                               `th-${column.value}`]"
                    @click="changeSort(column.value)">
                  {{ column.text }}
                  <button class="sort"
                          :class="{ active: listData.length && computedSort === column.value,
                                    asc: order,
                                    desc: !order }">
                    <i class="icon icon--sort-asc"></i>
                    <i class="icon icon--sort-desc"></i>
                  </button>
                </th>
              </tr>
            </thead>
          </table>
        </div>
        <div class="tbody"
             ref="tagList">
          <table>
            <colgroup>
              <col>
              <col style="width: 140px">
            </colgroup>
            <tbody>
              <tr v-for="(tag, idx) in realListData"
                  :key="`tag-item-${tag.tagId}`"
                  :class="{ active: active && tag.tagId === active.tagId, upcoming: tag.tagId === -1 }"
                  @mouseleave="menuOpen = -1">
                <template v-for="column in columns">
                  <td :class="`td-${column.value}`"
                      :key="`tag-item-${tag.tagId}-${column.text}-${column.value}`"
                      @click="setGroup(tag)">
                    <div v-if="tag.tagId === -1"
                         class="td-data">
                      <template v-if="column.value === 'tagName'">
                        <span class="bullet"></span>
                        <span class="fg">{{ $t('group.upcoming') }}</span>

                        <div class="schedule"
                             :class="{ updating }">
                          <div v-if="updating"
                               class="updating">
                            <em>
                              <i class="icon icon--renew"></i>
                              {{ $t('group.renewing') }}
                            </em>
                            <span v-html="$t('group.renewingAt', { date: renewingDateString })"></span>
                          </div>
                          <template v-else>
                            <span v-if="group.tagUpdateDate">
                              <ui-checkbox :value="scheduled"
                                           @input="onSchedule($event)">
                                {{ $t('group.renew') }}
                                <i class="icon icon--renew"></i>
                              </ui-checkbox>
                            </span>
                            <span v-else>
                              <ui-checkbox :value="true"
                                           :disabled="true">
                                {{ $t('group.renew') }}
                                <i class="icon icon--renew"></i>
                              </ui-checkbox>
                            </span>
                          </template>
                        </div>
                      </template>
                      <template v-else-if="column.value === 'numOfUrl'">
                        <span class="count">
                          <i class="icon icon--upcoming-white"></i>
                          {{ tag.numOfUrl | thousand }}

                          <span v-if="tag.recentUrlCount > 0"
                                class="badge"></span>
                        </span>

                        <transition name="fade">
                          <div class="schedule"
                               v-if="scheduled && !updating"
                               v-html="$t('group.renewAt', { date: renewDateString })"></div>
                        </transition>
                      </template>
                    </div>
                    <div v-else
                         class="td-data">
                      <template v-if="group.groupId === 1 && column.value === 'tagName'">
                        <span class="bullet"
                              :style="{ 'background-color': tag.colorHex }"></span>
                        <span class="fg">{{ lastSlash(tag.tagName) | onlySubdomain(journeyOptions.domain) }}</span>
                        <span class="bg">{{ lastSlash(tag.tagName) | onlySubdomain(journeyOptions.domain) }}</span>
                      </template>
                      <template v-else-if="column.value === 'tagName'">
                        <span class="bullet"
                              :style="{ 'background-color': tag.tagId === null || tag.tagId < 0 ? null : tag.colorHex }"></span>
                        <template v-if="!editing || editing.tagId !== tag.tagId">
                          <template v-if="tag.tagId === -2 || tag.tagId === null">
                            <span class="fg">{{ $t('group.upcoming') }}</span>
                            <span class="bg">{{ $t('group.upcoming') }}</span>
                          </template>
                          <template v-else>
                            <span class="fg">{{ tag.tagName }}</span>
                            <span class="bg">{{ tag.tagName }}</span>
                          </template>
                        </template>
                        <template v-else>
                          <span class="input">
                            <input type="text"
                                   v-model="tag.tagNameEditing"
                                   :id="`editName${tag.tagId}`"
                                   :class="{ error: editingError }"
                                   :placeholder="$t('group.editMode.insertTagName')"
                                   @click.stop
                                   @blur="upsertTagName(tag, true)"
                                   @keyup="editingError = ''"
                                   @keyup.esc="upsertTagName(tag)"
                                   @keyup.enter="$event.target.blur()" />
                            <i v-if="!editingError"
                               class="icon icon--close14"
                               @click.stop="upsertAlert(tag, 'clear')"></i>
                            <ui-icon-tooltip v-if="editingError"
                                             :iconSize="'red'"
                                             :arrowTop ="hasNoTag"
                                             :arrowBottom="!hasNoTag"
                                             arrowCenter>
                              <span v-if="editingError === 'duplicate'"
                                    v-html="$t('group.editMode.duplicatedTagName')"></span>
                            </ui-icon-tooltip>
                          </span>
                        </template>
                      </template>
                      <template v-else-if="column.value === 'numOfUrl' && tag.numOfUrl !== false">
                        <span v-if="!editing || editing.tagId !== tag.tagId"
                              class="count">
                          {{ tag.numOfUrl | thousand }}

                          <span v-if="tag.recentUrlCount > 0"
                                class="badge"></span>
                        </span>
                        <span v-if="(!editing || editing.tagId !== tag.tagId) && group.groupId > 1 && tag.tagId > -1"
                              class="tag-menu">
                          <span class="opener"
                                @click.stop="openMenu(tag.tagId)">
                            <i class="icon icon--hamburger"></i>
                          </span>
                          <span class="menu-ul"
                                :class="{ open: menuOpen === tag.tagId,
                                          middle: lastIdx - 1 === idx,
                                          last: lastIdx - 1 < idx }"
                                @mouseenter="resetDropdownTimer"
                                @mouseleave="startDropdownTimer"
                                @scroll.stop>
                            <span class="menu-li"
                                  :class="{disabled: isDemo}"
                                  @click.stop="colorTagName(tag)">
                              <i class="icon icon--color"></i>
                              {{ $t('group.editMode.changeColor') }}
                            </span>
                            <span class="menu-li"
                                  :class="{disabled: isDemo}"
                                  @click.stop="editTagName(tag)">
                              <i class="icon icon--edit"></i>
                              {{ $t('group.editMode.rename') }}
                            </span>
                            <span class="menu-li"
                                  :class="{disabled: isDemo}"
                                  @click.stop="deleteTagName(tag)">
                              <i class="icon icon--delete"></i>
                              {{ $t('group.editMode.delete') }}
                            </span>
                          </span>
                        </span>
                      </template>
                      <template v-else>
                        &nbsp;
                      </template>
                    </div>
                  </td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="list-bottom">
        <ui-navigation class="tag-navigation"
                       :currentPage="listOption.pageNum"
                       :lastPage="lastPage"
                       :disabled="updating"
                       @setPage="setPage" />
        <span class="view-per-page">
          {{ $t('common.pagination.viewPerPage') }} : {{ listOption.itemsPerPage }}
        </span>
      </div>
    </template>

    <dimmed v-if="alerting">
      <alert v-if="alerting === 'delete'"
             :icon="'icon--trash-large'"
             :title="$t('group.editMode.confirmDeleteTag.title')"
             :description="$t('group.editMode.confirmDeleteTag.description')"
             :cancel="$t('common.cancel')"
             :submit="$t('common.delete')"
             @onSubmit="deleteTag"
             @onCancel="cancelAlert" />

      <tag-color v-if="alerting === 'color'"
                 :original="{ colorId: tagInHand.colorId, colorHex: tagInHand.colorHex }"
                 :colorList="colorList"
                 @onSubmit="colorTag"
                 @onCancel="cancelAlert" />
    </dimmed>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { EventBus } from '@/libs/event-bus'
import { parse, format, addDays, addSeconds, addMinutes } from 'date-fns'

import { getTagList, getSearch, setSchedule, upsertTag, deleteTag, checkTagDuplication } from '@/api/group'

import TagColor from '@/components/group/TagColor'
import Alert from '@/components/ui/Alert'
import Dimmed from '@/components/ui/Dimmed'
import UiIconTooltip from '@/components/ui/UiIconTooltip'
import UiCheckbox from '@/components/ui/UiCheckbox'
import UiNavigation from '@/components/ui/UiNavigation'

export default {
  name: 'TagList',
  components: {
    TagColor,
    Alert,
    Dimmed,
    UiIconTooltip,
    UiCheckbox,
    UiNavigation
  },
  props: ['noResult', 'group', 'active', 'searchParam', 'count', 'colorList', 'urlsPerPage'],
  computed: {
    ...mapState([
      'journeyOptions',
      'isDemo'
    ]),
    sid () {
      return this.$route.query.sid
    },
    order () {
      return !this.listOption.sort.includes('-')
    },
    lastIdx () {
      return Math.min(this.listOption.itemsPerPage, this.realListData.length - 1)
    },
    computedSort () {
      return this.listOption.sort.replace('-', '')
    },
    renewDateString () {
      const loadedAt = addDays(new Date(), 1)
      const formatText = this.$t('group.renewAtFormat')
      return format(loadedAt, formatText)
    },
    renewingDateString () {
      let loadedAt = parse(this.group.taggingStartDate)
      loadedAt = addSeconds(loadedAt, this.group.estimateTime)
      const targetMin = Math.ceil(loadedAt.getMinutes() / 5) * 5
      loadedAt = addMinutes(loadedAt, targetMin - loadedAt.getMinutes())
      const formatText = this.$t('group.renewFinishAtFormat')
      return format(loadedAt, formatText)
    },
    updating () {
      return this.group && this.group.taggingStatus === 'PROCESSING'
    },
    realListData () {
      if (this.searchParam && this.searchCount > -1) {
        const first = (this.listOption.pageNum - 1) * this.listOption.itemsPerPage
        const last = this.listOption.pageNum * this.listOption.itemsPerPage
        return this.listData.slice(first, last)
      } else {
        return this.listData
      }
    },
    columns () {
      return [
        {
          text: this.group && this.group.groupId > 1 ? this.$t('group.column.tagName') : this.$t('group.column.subDomainWithPathname'),
          value: 'tagName'
        },
        {
          text: this.$t('group.column.page'),
          value: 'numOfUrl'
        }
      ]
    }
  },
  created () {
    EventBus.$on('reloadTagList', () => {
      this.loadTagList(true)
    })

    if (this.group) {
      this.scheduled = this.group.scheduleTag || false
      this.$emit('onSchedule', this.scheduled)
      this.loadTagList().then(() => {
        if (this.listData.length) {
          this.setGroup(this.listData[0])
        }
      })
    }
    this.calcLastPage()
  },
  watch: {
    group (value) {
      this.scheduled = value.scheduleTag || false
      this.$emit('onSchedule', this.scheduled)
      this.listOption = {
        pageNum: 1,
        itemsPerPage: 50,
        sort: 'tagName'
      }
      this.loadTagList().then(() => {
        if (this.listData.length) {
          this.setGroup(this.listData[0])
        } else {
          this.$emit('onTagChange', null)
        }
      })
    },
    searchParam (value, prev) {
      let v = value ? value.term + value.text : value
      let p = prev ? prev.term + prev.text : prev

      if (v !== p) {
        this.listOption = {
          pageNum: 1,
          itemsPerPage: this.listOption.itemsPerPage,
          sort: 'tagName'
        }
        this.loadTagList().then(() => {
          if (this.listData.length) {
            this.setGroup(this.listData[0])
          } else {
            this.$emit('onTagChange', null)
          }
        })
      }
    },
    count () {
      this.calcLastPage()
    }
  },
  methods: {
    loadTagList (force = true) {
      if (this.searchParam) {
        return this.loadSearchList()
      }
      this.editing = null
      this.searchCount = -1
      this.hasNoTag = false
      this.editingError = null
      EventBus.$emit('onLongLoading', true)
      this.$emit('searchCount', this.searchCount)
      return new Promise(resolve => {
        getTagList({
          sid: this.sid,
          pageId: this.listOption.pageNum - 1,
          pageNum: this.listOption.itemsPerPage,
          sort: this.listOption.sort,
          groupId: this.group.groupId
        }, force).then(result => {
          this.listData = []
          let hasActive = false
          for (let tag of result) {
            if (tag.tagId > -1 || tag.numOfUrl > 0) {
              const nowOn = {
                tagId: tag.tagId,
                colorHex: tag.colorHex,
                tagName: tag.tagName,
                tagNameEditing: tag.tagName,
                numOfUrl: tag.numOfUrl,
                recentUrlCount: tag.recentUrlCount || 0
              }
              this.listData.push(nowOn)

              if (this.active && this.active.tagId === tag.tagId) {
                hasActive = true
                this.setGroup(nowOn)
              }
            }

            if (tag.tagId === -2 && tag.numOfUrl === 0) {
              this.hasNoTag = true
            }
          }
          if (!hasActive) {
            this.setGroup(this.listData[0])
          }
          this.sortInList()
          this.calcLastPage()
          setTimeout(() => {
            EventBus.$emit('onLongLoading', false)
          }, 500)

          resolve()
        })
      })
    },

    loadSearchList (force = true) {
      EventBus.$emit('onLongLoading', true)
      return new Promise(resolve => {
        getSearch({
          sid: this.sid,
          pageId: this.listOption.pageNum - 1,
          pageNum: this.urlsPerPage,
          term: this.searchParam.term,
          text: this.searchParam.text,
          sort: this.listOption.sort,
          groupId: this.group.groupId,
          tagId: null
        }, force).then(result => {
          this.listData = []
          this.searchCount = 0
          let hasActive = false
          for (let tag of result) {
            const nowOn = {
              tagId: tag.tagId,
              colorHex: tag.colorHex,
              tagName: tag.tagName,
              tagNameEditing: tag.tagName,
              numOfUrl: tag.numOfUrl,
              recentUrlCount: tag.recentUrlCount || 0,
              urls: tag.urls
            }
            this.listData.push(nowOn)
            this.searchCount += tag.numOfUrl

            if (this.active && this.active.tagId === tag.tagId) {
              hasActive = true
              this.setGroup(nowOn)
            }
          }
          if (!hasActive) {
            this.setGroup(this.listData[0])
          }
          this.sortInList()
          this.calcLastPage()
          EventBus.$emit('onLongLoading', false)
          this.$emit('searchCount', this.searchCount)
          if (result.length < 1) {
            this.$emit('noResult', true)
          } else {
            this.$emit('noResult', false)
          }
          resolve()
        })
      })
    },

    setNextColor () {
      return new Promise(resolve => {
        let colors = [...this.colorList]
        let usingColors = this.listData.map(item => item.colorHex)
        let usingColorCount = {}
        for (const color of colors) {
          usingColorCount[color.colorHex] = usingColors.filter(item => item === color.colorHex).length
        }
        let minimumCount = Object.values(usingColorCount).reduce((min, item) => Math.min(min, item), Number.POSITIVE_INFINITY)

        for (const [colorHex, count] of Object.entries(usingColorCount)) {
          if (count === minimumCount) {
            this.nextColor = colors.find(item => item.colorHex === colorHex)
            break
          }
        }
        resolve()
      })
    },

    calcLastPage () {
      if (this.updating) {
        this.lastPage = 1
      } else {
        if (this.searchParam) {
          this.lastPage = Math.ceil(this.listData.length / this.listOption.itemsPerPage)
        } else {
          if (this.count && typeof this.count.tag === 'number') {
            this.lastPage = Math.ceil(this.count.tag / this.listOption.itemsPerPage)
          } else {
            this.lastPage = 0
          }
        }
      }
    },

    lastSlash (tag) {
      if (!tag) {
        tag = ''
      }
      if (tag.substr(-1, 1) === '/') {
        return tag
      }
      return tag + '/'
    },

    setPage (page) {
      this.listOption.pageNum = page
      if (!this.searchParam) {
        this.loadTagList().then(() => {
          this.$refs.tagList.scrollTop = 0
        })
      } else {
        this.$refs.tagList.scrollTop = 0
      }
    },

    changeSort (sort) {
      if (this.listOption.sort === '-' + sort) {
        this.listOption.sort = sort
      } else {
        this.listOption.sort = '-' + sort
      }
      if (this.lastPage > 1) {
        this.loadTagList()
      } else {
        this.sortInList()
      }
    },

    sortInList () {
      this.listData.sort((a, b) => {
        if (a.tagId < 0) {
          return -1
        }
        if (b.tagId < 0) {
          return 1
        }
        if (typeof a[this.computedSort] === 'string' && typeof b[this.computedSort] === 'string') {
          if (this.order) {
            // return a[this.computedSort].toString().localeCompare(b[this.computedSort].toString())
            return new Intl.Collator('en').compare(a[this.computedSort].toString(), b[this.computedSort].toString())
          } else {
            // return b[this.computedSort].toString().localeCompare(a[this.computedSort].toString())
            return new Intl.Collator('en').compare(b[this.computedSort].toString(), a[this.computedSort].toString())
          }
        } else {
          if (this.order) {
            return a[this.computedSort] > b[this.computedSort] ? 1 : -1
          } else {
            return b[this.computedSort] > a[this.computedSort] ? 1 : -1
          }
        }
      })
    },

    setGroup (tag) {
      this.$emit('onTagChange', { ...tag })
    },

    onSchedule (value) {
      this.scheduled = value

      setSchedule({
        sid: this.sid,
        groupId: this.group.groupId,
        schedule: this.scheduled
      }, true)
    },

    openMenu (id) {
      this.menuOpen = id
      this.startDropdownTimer()
    },

    startDropdownTimer () {
      this.resetDropdownTimer()
      this.dropdownTimer = setInterval(() => {
        if (this.dropdownTimerTime < 3) {
          this.menuOpen = -1
        }
        this.dropdownTimerTime--
      }, 1000)
    },

    resetDropdownTimer () {
      this.dropdownTimerTime = 3
      clearInterval(this.dropdownTimer)
      this.dropdownTimer = null
    },

    editTagName (tag) {
      if (this.isDemo) return false
      this.editing = tag
      if (tag) {
        this.editingError = null
        tag.tagNameEditing = tag.tagName
      }

      if (tag) {
        this.menuOpen = -1
        setTimeout(() => {
          document.getElementById(`editName${tag.tagId}`).focus()
        }, 0)
      }
    },

    upsertTagName (tag, submit = false) {
      setTimeout(() => {
        if (!submit) {
          tag.tagNameEditing = tag.tagName
        }
        if (this.editingError) {
          submit = false
        }
        if (this.editing) {
          if (tag.tagNameEditing === '' || tag.tagNameEditing === tag.tagName) {
            submit = false
          }
          if (tag.tagId !== false) {
            if (this.clearing) {
              tag.tagNameEditing = ''
            } else if (!submit) {
              tag.tagNameEditing = tag.tagName
              this.editTagName(null)
            } else {
              this.checkTagNameDuplication(tag.tagNameEditing).then(() => {
                this.editTagName(null)
                tag.tagName = tag.tagNameEditing
                upsertTag({
                  sid: this.sid,
                  groupId: this.group.groupId,
                  tagId: tag.tagId,
                  tagName: tag.tagName
                })
              }).catch(() => {
                document.getElementById(`editName${tag.tagId}`).focus()
                this.editingError = 'duplicate'
              })
            }
            if (this.clearing) {
              document.getElementById(`editName${tag.tagId}`).focus()
              this.clearing = false
            }
          } else {
            if (!submit) {
              this.listData.splice(this.listData.findIndex(item => item.tagId === false), 1)
              this.editTagName(null)
            } else {
              this.checkTagNameDuplication(tag.tagNameEditing).then(() => {
                upsertTag({
                  sid: this.sid,
                  groupId: this.group.groupId,
                  tagName: tag.tagNameEditing,
                  colorId: tag.colorId,
                  colorHex: tag.colorHex
                }).then(() => {
                  EventBus.$emit('reloadGroupStat')
                  this.loadTagList(true)
                })
              }).catch(() => {
                document.getElementById(`editName${tag.tagId}`).focus()
                this.editingError = 'duplicate'
              })
            }
          }
        }
      }, 100)
    },

    upsertAlert (tag, alert) {
      if (alert === 'clear') {
        this.clearing = true
      }
    },

    deleteTagName (tag) {
      if (this.isDemo) return false
      this.tagInHand = tag
      this.alerting = 'delete'
    },

    deleteTag () {
      EventBus.$emit('onLoading', true)
      deleteTag({
        sid: this.sid,
        groupId: this.group.groupId,
        tagId: this.tagInHand.tagId
      }).then(() => {
        EventBus.$emit('onLoading', false)
        this.loadTagList(true)
        EventBus.$emit('reloadGroupStat')
      })
      this.tagInHand = null
      this.alerting = null
    },

    cancelAlert () {
      this.tagInHand = null
      this.alerting = null
    },

    colorTagName (tag) {
      if (this.isDemo) return false
      this.setNextColor().then(() => {
        this.tagInHand = tag
        this.alerting = 'color'
      })
    },

    colorTag (color) {
      this.tagInHand.colorId = color.colorId
      this.tagInHand.colorHex = color.colorHex
      upsertTag({
        sid: this.sid,
        groupId: this.group.groupId,
        tagId: this.tagInHand.tagId,
        colorId: this.tagInHand.colorId
      })
      this.cancelAlert()
    },

    checkTagNameDuplication (tagName) {
      return new Promise((resolve, reject) => {
        if (['미태그', 'Untagged', 'タグ無し'].includes(tagName)) {
          reject(new Error('untagged'))
        } else {
          checkTagDuplication({
            sid: this.sid,
            groupId: this.group.groupId,
            tagName
          }, true).then(result => {
            if (result.duplicate) {
              reject(new Error('duplicate'))
            } else {
              resolve()
            }
          })
        }
      })
    },

    addTagName () {
      if (this.isDemo) return false
      this.editingError = null
      this.setNextColor().then(() => {
        if (!this.listData.find(item => item.tagId === false)) {
          const newTag = {
            tagId: false,
            colorId: this.nextColor.colorId,
            colorHex: this.nextColor.colorHex,
            tagName: '',
            tagNameEditing: '',
            numOfUrl: false,
            urls: null
          }
          if (this.listData.length && (this.listData[0].tagId === null || this.listData[0].tagId < 0)) {
            this.listData.splice(1, 0, newTag)
          } else {
            this.listData.unshift(newTag)
          }
          this.editing = newTag
          setTimeout(() => {
            document.getElementById('editNamefalse').focus()
          }, 0)
        }
      })
    }
  },
  data () {
    return {
      listOption: {
        pageNum: 1,
        itemsPerPage: 50,
        sort: 'tagName'
      },
      lastPage: 0,

      scheduled: false,
      scheduleTooltipTimer: null,

      listData: [],
      searchCount: 0,
      menuOpen: -1,
      dropdownTimer: null,
      dropdownTimerTime: null,

      editing: false,
      editingError: null,
      alerting: null,
      tagInHand: null,
      clearing: false,

      nextColor: {},
      hasNoTag: false
    }
  }
}
</script>

<style lang="scss" scoped>
.tag-list {
  position: relative;
  height: 100%;

  .list-top {
    z-index: 2;
    position: relative;
    margin: 0 -8px;
    padding: 2px 0;
    height: 49px;

    &:hover {
      z-index: auto;
    }

    .add-tag {
      font-size: 14px;
      line-height: 1;
      color: #444444;
      padding: 5px;
      margin: 12px 15px;
      transition: color 0.2s ease;
      font-weight: 500;

      &.disabled {
        pointer-events: none;
        opacity: 0.5;
      }

      .tooltip {
        vertical-align: middle;
        margin-right: 2px;

        & /deep/ .bubble {
          z-index: 10;
        }
      }

      svg {
        vertical-align: middle;
        margin-top: -4px;
        margin-left: 2px;

        g {
          fill: #666666;
          transition: fill 0.2s ease;
        }
      }

      &:hover {
        color: #ec0047;

        svg {
          g {
            fill: #ec0047;
          }
        }
      }
    }
  }

  .list-body {
    z-index: 1;
    position: relative;
    height: calc(100% - 106px);

    &::after {
      content: "";
      position: absolute;
      z-index: 5;
      bottom: 0;
      right: 0;
      display: block;
      width: calc(100% + 10px);
      height: 6px;
      opacity: 0.06;
      background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0), #000);
    }

    .thead {
      height: 55px;
    }

    .tbody {
      position: relative;
      height: calc(100% - 67px);
      padding-bottom: 12px;
      overflow: auto;

      table {
        display: block;
      }
    }

    table {
      position: relative;
      width: 100%;

      thead {
        tr {
          th {
            height: 39px;
            font-size: 13px;
            font-weight: normal;
            background: #375f7b;
            color: #ffffff;
            padding: 9px 32px 7px 21px;
            vertical-align: bottom;
            text-align: left;
            line-height: 20px;
            transition: background-color 0.15s ease;
            white-space: nowrap;

            .sort {
              display: inline-block;
              width: 14px;
              height: 18px;
              margin-top: -2px;
              margin-left: 2px;
              vertical-align: middle;
              border-radius: 4px;
              transition: background-color 0.15s ease,
                          box-shadow 0.15s ease;

              i.icon {
                float: left;
                opacity: 0.2;
                transition: opacity 0.15s ease;
              }

              &.active {
                &.asc {
                  .icon--sort-asc {
                    opacity: 1;
                  }
                }

                &.desc {
                  .icon--sort-desc {
                    opacity: 1;
                  }
                }
              }

              &.active {
                background-color: #272727;
                box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.146);
              }
            }

            &.th-numOfUrl {
              text-align: right;
            }

            &.hover {
              .sort {
                background-color: #272727;
                box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.146);
              }
            }

            &:first-child {
              border-radius: 7px 0 0 7px;
            }
          }
        }
      }

      tbody {
        display: block;
        width: 100%;

        tr {
          display: table;
          position: relative;
          width: 100%;

          &:hover {
            td {
              .td-data {
                background-color: #ebebeb;

                .count {
                  border-color: #666;
                  background-color: #666;
                }

                .tag-menu {
                  width: 25px;
                  opacity: 1;
                }
              }
            }
          }

          &::after {
            content: "";
            display: block;
            position: absolute;
            top: 7px;
            left: 5px;
            right: 15px;
            z-index: -1;
            border-radius: 5px;
            height: calc(100% - 14px);
            transition: box-shadow 0.2s ease;
          }

          &.active {
            &::after {
              box-shadow: 0 3px 4px -2px rgba(0, 0, 0, 0.2), inset 0 0 0 2px #ea0046;
            }

            td {
              .td-data {
                background-color: transparent;
              }

              &.td-tagName {
                .td-data {
                  height: auto;
                  max-height: 500px;

                  .fg {
                    color: transparent;
                  }

                  .bg {
                    color: #666;
                  }
                }
              }

              &.td-numOfUrl {
                .count {
                  border-color: rgba(0, 0, 0, 0.22);
                  background-color: #ffffff;
                  color: #444444;
                }
              }
            }
          }

          td {
            text-align: left;
            font-size: 13px;
            font-weight: 500;
            line-height: 1.31;
            letter-spacing: normal;
            color: #666;
            word-break: break-all;
            cursor: pointer;
            vertical-align: top;

            .td-data {
              position: relative;
              min-height: 20px;
              padding: 11px 11px 9px;
              margin: 7px 0;
              cursor: pointer;
            }

            &.td-tagName {
              padding-left: 5px;

              .td-data {
                padding-left: 35px;
                height: 20px;
                max-height: 20px;
                overflow: visible;
                transition: max-height 0.5s ease;

                .fg {
                  position: absolute;
                  top: 11px;
                  left: 35px;
                  width: calc(100% - 45px);
                  display: block;
                  height: 18px;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  transition: color 0.3s ease;
                }

                .bg {
                  display: block;
                  color: transparent;
                  transition: color 0.3s ease;
                }
              }

              .bullet {
                position: absolute;
                top: 14px;
                left: 15px;
                display: inline-block;
                width: 10px;
                height: 10px;
                border: solid 1px rgba(0, 0, 0, 0.1);
                background-color: #bbbbbb;
                border-radius: 12px;
                vertical-align: middle;
              }

              .input {
                position: relative;

                input {
                  padding: 7px 23px 7px 8px;
                  border-radius: 4px;
                  border: solid 1px #289eef;
                  background-color: #fff;
                  font-size: 12px;
                  line-height: 14px;
                  color: #2f2f2f;
                  margin: -6px 0 -5px;
                  width: 179px;
                  max-width: calc(100% - 33px);
                  transition: border-color 0.15s ease;

                  &.error {
                    border-color: #e60724;
                  }
                }

                .icon {
                  position: absolute;
                  top: 1px;
                  right: 6px;
                }

                & /deep/ .tooltip {
                  .icon {
                    position: absolute;
                    top: 0.5px;
                    right: 6px;
                  }

                  .bubble {
                    bottom: 7px !important;
                    margin-left: -15px !important;
                    display: block !important;
                    opacity: 1 !important;
                    padding-bottom: 10px;

                    .text {
                      font-weight: normal;
                      color: #ec0047;
                    }
                  }
                }
              }
            }

            &.td-numOfUrl {
              text-align: right;
              padding-right: 15px;
              white-space: nowrap;

              .td-data {
                padding-right: 14px;
              }

              .count {
                position: relative;
                top: -1px;
                display: inline-block;
                border-radius: 20px;
                background-color: #b1b1b1;
                border: 1px solid #b1b1b1;
                color: #ffffff;
                line-height: 1;
                padding: 3px 5px;
                font-weight: normal;
                text-align: center;
                min-width: 8px;
                font-size: 12px;
                white-space: nowrap;

                .badge {
                  position: absolute;
                  display: block;
                  top: -2px;
                  right: -4px;
                  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.75);
                  background-color: #ea0046;
                  width: 8px;
                  height: 8px;
                  border-radius: 8px;
                }
              }

              .tag-menu {
                position: relative;
                display: inline-block;
                vertical-align: middle;
                height: 20px;
                margin: -1px 0;
                width: 0;
                opacity: 0;
                transition: width 0.2s ease,
                            opacity 0.2s ease;

                .opener {
                  display: inline-block;
                  vertical-align: middle;
                  margin: -4px -5px 0 5px;
                  padding: 2px;
                  cursor: pointer;
                  width: 20px;
                  height: 20px;
                  border-radius: 20px;
                  transition: background-color 0.2s ease;

                  &:hover {
                    background-color: rgba(0, 0, 0, 0.1);
                  }
                }

                .menu-ul {
                  position: absolute;
                  z-index: 100;
                  right: 23px;
                  top: -5px;
                  padding: 5px 0;
                  border-radius: 4px;
                  box-shadow: 0 12px 15px -6px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(0, 0, 0, 0.25);
                  background-color: #fff;
                  width: 135px;
                  transform: scale(0);
                  transform-origin: right top;
                  transition: transform 0.2s ease;

                  &.open {
                    transform: scale(1);
                  }

                  &.last {
                    top: auto;
                    bottom: -5px;
                    transform-origin: right bottom;
                  }

                  &.middle {
                    top: calc(-50% - 33px);
                    transform-origin: right center;
                  }

                  .menu-li {
                    cursor: pointer;
                    display: block;
                    text-align: left;
                    font-size: 12px;
                    font-weight: normal;
                    color: #333333;
                    line-height: 30px;
                    transition: background-color 0.15s ease;
                    margin: 2px 0;

                    &.disabled {
                      pointer-events: none;
                      opacity: 0.5;
                    }

                    .icon {
                      vertical-align: middle;
                      margin: -1px 5px 0 10px;
                    }

                    &:hover {
                      background-color: #ebebeb;
                    }
                  }
                }
              }
            }

            &:first-child {
              .td-data {
                border-radius: 5px 0 0 5px;
              }
            }

            &:last-child {
              .td-data {
                border-radius: 0 5px 5px 0;
              }
            }
          }

          &.upcoming {
            overflow: hidden;

            td {
              padding-top: 8px;
              padding-bottom: 7px;
              color: #444;

              &::after {
                content: "";
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
                height: 1px;
                background-color: #ebebeb;
              }

              .td-data {
                background: transparent;
                max-height: 500px;
              }

              &.td-tagName {
                overflow: visible;
                z-index: 10;
                width: 100%;

                .td-data {
                  padding-top: 13px;
                  padding-left: 13px;
                  overflow: visible;

                  .fg {
                    color: #444 !important;
                  }

                  .schedule {
                    padding-top: 43px;
                    font-weight: 500;
                    line-height: 1;
                    color: #444;
                    min-height: 0;
                    transition: min-height 0.3s ease;

                    &.updating {
                      min-height: 25px;
                    }

                    .updating {
                      position: absolute;
                      top: 50px;
                      left: 0;
                      width: 340px;
                      text-align: center;
                      font-size: 12px;
                      line-height: 1;
                      color: #666;
                      font-weight: normal;

                      em {
                        display: block;
                        font-size: 14px;
                        font-weight: 500;
                        line-height: 1;
                        color: #444;
                        margin-bottom: 7px;
                      }
                    }

                    .ui_checkbox {
                      margin-left: 0;

                      & /deep/ .text {
                        color: #555;

                        .icon {
                          position: static;
                          transform: none;
                        }
                      }
                    }

                    .icon {
                      vertical-align: middle;
                      margin-top: -3px;
                    }
                  }
                }

                &::after {
                  left: 10px;
                  width: calc(100% - 20px);
                }
              }

              &.td-numOfUrl {
                height: 97px;

                .count {
                  border-color: #444;
                  background-color: #444;
                  color: #ffffff;

                  .icon {
                    vertical-align: middle;
                    margin: -2px 1px -1px -2px;
                  }
                }

                .schedule {
                  padding-top: 11px;
                  font-weight: normal;
                  line-height: 1.33;
                  font-size: 12px;
                  white-space: nowrap;

                  &.fade-enter-active,
                  &.fade-leave-active {
                    opacity: 1;
                    transition: opacity 0.15s ease;
                  }
                  &.fade-enter,
                  &.fade-leave-to{
                    opacity: 0;
                  }
                }

                &::after {
                  width: calc(100% - 20px);
                }
              }
            }
          }
        }
      }
    }
  }

  .list-bottom {
    z-index: 2;
    position: relative;
    padding: 1px 0 2px 8px;
    margin: 0 0 0 -10px;
    height: 49px;
    border-top: 1px solid rgba(0, 0, 0, 0.16);

    .tag-navigation {
      float: left;
    }

    .view-per-page {
      float: right;
      font-size: 12px;
      color: #767676;
      padding: 19px 16px 0 0;
    }
  }
}
</style>
